
import { defineComponent } from 'vue'
import { Form, Field, defineRule } from 'vee-validate'
import { APIClient } from '@/lib/api-client'
import { Campaigns, Status as CampaignStatus } from '@/lib/campaigns'
import { Campaign, Variant, PlainBodyUrl, ThumbnailUrl, User } from '@/types'
import { formatDatetime, formatPrice, formatDate, formatTime, useItemColorFormatter, ItemColorFormatter } from '@/lib/formatters'
import { required } from '@vee-validate/rules'

import SuzuriHeader from '@/components/SuzuriHeader.vue'
import ItemSpec from '@/components/ItemSpec.vue'
import ThumbnailCaption from '@/components/ThumbnailCaption.vue'

interface Data {
  campaign: Campaign | null;
  selectedVariant: Variant | null;
  itemSizeId: number | null;
  colorSelectorVisbile: boolean;
  shareSheetVisible: boolean;
  campaignStatus: CampaignStatus | null;
  remainingSeconds: number | null;
  selectedScene: string | null;
  user: User | null;
}

defineRule('required', required)

export default defineComponent({
  components: {
    Form,
    Field,
    SuzuriHeader,
    ItemSpec,
    ThumbnailCaption
  },
  data (): Data {
    return {
      campaign: null,
      selectedVariant: null,
      itemSizeId: null,
      colorSelectorVisbile: false,
      shareSheetVisible: false,
      campaignStatus: null,
      remainingSeconds: null,
      selectedScene: null,
      user: null
    }
  },
  computed: {
    buttonLabel (): string {
      switch (this.campaignStatus) {
        case 'unstarted':
          return '予約開始までお待ちください'
        case 'started':
          return this.isSample ? 'このアイテムをサンプル発注する' : 'このアイテムを予約する'
        case 'ended':
          return '予約期間は終了しました'
        default:
          return ''
      }
    },
    buttonDisabled (): boolean {
      return this.campaignStatus !== 'started'
    },
    suzuriShopUrl (): string {
      return this.campaign ? `/${this.campaign.user.name}` : ''
    },
    tweetUrl (): string {
      if (this.campaign) {
        const query = new URLSearchParams({ url: location.href })
        return `https://x.com/intent/tweet?hashtags=SUZURIのシルクスクリーン&text=${this.campaign.name} ∞ SUZURI&${query}`
      } else {
        return ''
      }
    },
    twitterUrl (): string {
      if (this.campaign && this.campaign.user.twitterName) {
        return `https://x.com/@${this.campaign.user.twitterName}`
      } else {
        return ''
      }
    },
    lineUrl (): string {
      if (this.campaign) {
        const text = `${this.campaign.name} ∞ SUZURI ${location.href}`
        return `https://line.me/R/msg/text/${text}`
      } else {
        return ''
      }
    },
    scenes (): string[] {
      return this.campaign?.item?.scenes || []
    },
    mainThumbnailSize () {
      return window.innerWidth <= 375 ? 'medium' : 'large'
    },
    isScreenPrinting (): boolean {
      if (this.campaign) {
        return this.campaign.printingMethod === 'screen'
      } else {
        return false
      }
    },
    isSample (): boolean {
      return this.campaign?.sample || false
    },
    showAchievementBadge (): boolean {
      return this.isScreenPrinting && (this.selectedScene === 'front' || this.selectedScene === 'back')
    },
    isSweat (): boolean {
      return this.campaign?.item.name === 'sweat-united-athle-5044'
    },
    itemColorFormatter (): ItemColorFormatter {
      return useItemColorFormatter()
    }
  },
  async mounted () {
    const campaignIdMatches = location.href.match('squeegee/campaigns/(\\d+)')
    const campaignId = campaignIdMatches && campaignIdMatches[1]

    this.campaign = await APIClient.get<Campaign>(`campaigns/${campaignId}`).then(res => res.data)

    if (this.campaign) {
      this.selectedVariant = this.campaign.variants[0]
      this.selectedScene = this.selectedVariant.prints[0].place

      this.calcCampaignStatus()
      this.calcRemainingSeconds()

      // キャンペーンが始まったときにreloadせずに反映されるようにするためcampaignStatusはcomputedにせずdataで持つ
      setInterval(() => {
        this.calcCampaignStatus()
        this.calcRemainingSeconds()
      }, 1000 * 1)
    }
  },
  methods: {
    calcCampaignStatus () {
      this.campaignStatus = this.campaign && Campaigns.status(this.campaign)
    },
    calcRemainingSeconds () {
      if (this.campaign && this.campaignStatus === 'started') {
        const endAt = new Date(this.campaign.endAt).getTime()
        const diff = Math.round((endAt - Date.now()) / 1000)

        this.remainingSeconds = (diff > 0 && diff < 3 * 24 * 60 * 60) ? diff : null
      }
    },
    changeSelectedScene (scene: string) {
      this.selectedScene = scene
    },
    selectVariant (variant: Variant) {
      this.selectedVariant = variant
    },
    thumbnailUrl (scene: string, size: 'small' | 'medium' | 'large'): string | undefined {
      if (this.campaign && this.selectedVariant) {
        const print = this.selectedVariant.prints[0]
        const thumbnailUrl = print.thumbnailUrls.find((a: ThumbnailUrl) => a.scene === scene && a.size === size)
        if (thumbnailUrl) {
          return thumbnailUrl.url
        } else {
          const color = this.selectedVariant.itemColor
          return this.campaign.item.plainBodyUrls.find((a: PlainBodyUrl) => a.color === color.name && a.scene === scene)?.url
        }
      } else {
        return ''
      }
    },
    printedThumbnailUrl (variant: Variant, size: 'small' | 'medium' | 'large') {
      return variant.prints[0].thumbnailUrls.find(u => u.size === size)?.url
    },
    async goToCart (validate: () => Promise<{ valid: boolean; errors: Record<string, string>}>) {
      if (this.campaign && this.selectedVariant) {
        if ((await validate()).valid) {
          location.href = `/squeegee/campaigns/${this.campaign.id}/orders/new?variantId=${this.selectedVariant.id}&sizeId=${this.itemSizeId}`
        } else {
          const invalidInput = document.querySelector('.invalid')
          invalidInput?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }
    },
    formatDatetime,
    formatPrice,
    formatDate,
    formatTime,
    formatItemColor (variant: Variant) {
      return this.itemColorFormatter(variant)
    }
  }
})
